import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import { Phone, Email } from '@mui/icons-material';
import { useSettings } from '../../../hooks';
import { useNavigate } from '@reach/router';

const DEFAULT_FOOTER_CSS = {
  '& img': {
    maxWidth: '100px',
    maxHeight: '100px',
  },
};

export const PolicyStreamDefaultFooter = () => {
  const {
    settings: { companyName, phone, email, logo, footerLogo, footerCss },
  } = useSettings();
  const footerLogoToUse = footerLogo ?? logo;
  const navigate = useNavigate();

  return (
    <FooterContainer sx={footerCss ? footerCss : DEFAULT_FOOTER_CSS}>
      <TopRowContainer>
        {footerLogoToUse ? <img src={footerLogoToUse} alt='Logo' /> : null}
        <IconContainer>
          {email ? (
            <ContactItem>
              <IconBox>
                <Email fontSize='medium' />
              </IconBox>
              <Link
                href={`mailto:${email}`}
                color='inherit'
                underline='hover'
                variant='body1'
              >
                {email}
              </Link>
            </ContactItem>
          ) : null}
          {phone ? (
            <ContactItem>
              <IconBox>
                <Phone fontSize='medium' />
              </IconBox>
              <Link
                href={`tel:${phone}`}
                color='inherit'
                underline='hover'
                variant='body1'
              >
                {phone}
              </Link>
            </ContactItem>
          ) : null}
        </IconContainer>
      </TopRowContainer>

      <Typography variant='body2' gutterBottom>
        This document is provided for informational purposes only and does not
        constitute a binding contract or offer of life insurance coverage.
        Coverage is subject to underwriting approval and terms and conditions as
        set out in the final policy documentation.
      </Typography>
      <Typography variant='body2' gutterBottom>
        Please review your policy carefully and contact us with any questions or
        concerns.
      </Typography>

      <DottedDivider />

      <CalefyContainer>
        <Typography variant='body2'>
          <Link
            color='inherit'
            underline='none'
            onClick={() => navigate('/insurtech/manager/dashboard')}
          >
            Powered
          </Link>{' '}
          by{' '}
          <PolicyStreamLink href='https://policystream.ca'>
            PolicyStream
          </PolicyStreamLink>
        </Typography>
        <StyledCopyRight variant='body2'>
          Copyright © {new Date().getFullYear()} {companyName}
        </StyledCopyRight>
      </CalefyContainer>
    </FooterContainer>
  );
};

const IconContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    height: '100%',
    marginTop: theme.spacing(2),
  },
}));

const IconBox = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 48,
  height: 48,
  borderRadius: 8,
  backgroundColor: 'rgba(255, 255, 255, 0.15)', // translucent white background
  border: `1px solid ${
    theme.palette.tertiary
      ? theme.palette.tertiary.text
      : // @ts-expect-error
        theme.palette.secondary.text
  }`,
}));

const PolicyStreamLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main,
}));

const StyledCopyRight = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(1),
  },
}));

const TopRowContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  // height: theme.spacing(18),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'center',
    height: '100%',
  },
}));

const CalefyContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    alignSelf: 'center',
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
      height: '100%',
    },
  };
});

const DottedDivider = styled('div')(({ theme }) => ({
  borderTop: `1px dashed ${
    theme.palette.tertiary
      ? theme.palette.tertiary.text
      : // @ts-expect-error
        theme.palette.secondary.text
  }`,
  borderTopWidth: '2px',
  opacity: 0.15,
  marginTop: theme.spacing(6),
}));

const FooterContainer = styled('footer')(({ theme }) => ({
  backgroundColor: '#F9F6EE',
  color: 'black',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(0),
  paddingLeft: theme.spacing(10),
  paddingRight: theme.spacing(10),
  marginTop: theme.spacing(8),
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
}));

const ContactItem = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));
